// Permission system — mirrors backend src/auth/permissions.js
// Sync this file when BE permissions.js changes.

export const ROLE_IDS = {
  MANAGER: 1,
  DOCTOR: 2,
  COUNTER: 3,
  ASSISTANT: 4,
  HEADCOUNTER: 5,
  STOCK_MANAGER: 6,
  STOCK_STAFF: 7,
  RDM: 101,
  C_LEVEL: 102,
  ACCOUNTANT: 103,
  PROCUREMENT: 104,
};

export const ROLE_KEYS = Object.fromEntries(
  Object.entries(ROLE_IDS).map(([k, v]) => [v, k])
);

// Role-name constants (key → key) for referring to roles by symbol instead of
// raw strings, e.g. ROLES.MANAGER === "MANAGER".
export const ROLES = Object.fromEntries(
  Object.keys(ROLE_IDS).map((k) => [k, k])
);

export const BRANCH_ROLE_KEYS = [
  "MANAGER",
  "DOCTOR",
  "COUNTER",
  "ASSISTANT",
  "HEADCOUNTER",
  "STOCK_MANAGER",
  "STOCK_STAFF",
];

export const CENTRAL_ROLE_KEYS = [
  "RDM",
  "C_LEVEL",
  "ACCOUNTANT",
  "PROCUREMENT",
];

export const ROLE_LABELS_TH = {
  MANAGER: "ผู้จัดการสาขา",
  DOCTOR: "แพทย์",
  COUNTER: "เคาน์เตอร์",
  ASSISTANT: "ผู้ช่วย",
  HEADCOUNTER: "หัวหน้าเคาน์เตอร์",
  STOCK_MANAGER: "ผู้จัดการสต๊อก",
  STOCK_STAFF: "พนักงานสต๊อก",
  RDM: "ผู้จัดการเขต (RDM)",
  C_LEVEL: "ผู้บริหารระดับสูง (C-Level)",
  ACCOUNTANT: "บัญชี",
  PROCUREMENT: "จัดซื้อ",
};

// Short Thai descriptions shown under each role in the assign/remove dialogs so
// the operator understands what a role grants before choosing it.
export const ROLE_DESCRIPTIONS_TH = {
  MANAGER: "ดูแลภาพรวมสาขา · จัดการพนักงานและตารางงาน · เข้าถึงรายงานสาขา",
  DOCTOR: "เปิดเคสคนไข้ · บันทึกผลตรวจและการรักษา",
  COUNTER: "รับลงทะเบียนคนไข้ · จัดการนัดหมายและการชำระเงิน",
  ASSISTANT: "ช่วยงานข้างเก้าอี้ · เตรียมเคสและอุปกรณ์",
  HEADCOUNTER: "หัวหน้าทีมเคาน์เตอร์ · ดูแลงานหน้าร้านทั้งหมด",
  STOCK_MANAGER: "จัดการคลังสินค้าของสาขา · อนุมัติการเบิกจ่าย",
  STOCK_STAFF: "เบิก-จ่ายและตรวจนับสต๊อกประจำวัน",
  RDM: "ผู้จัดการเขต — ดูแลหลายสาขาที่กำหนดขอบเขตไว้",
  C_LEVEL: "ผู้บริหารระดับสูง — สิทธิ์เต็มทั้งคลินิก",
  ACCOUNTANT: "เข้าถึงข้อมูลการเงินและบัญชีระดับคลินิก",
  PROCUREMENT: "ดูแลการจัดซื้อจัดจ้างระดับคลินิก",
};

// Role-specific consequences surfaced in the remove dialog ("what they lose").
// Generic enough to stay accurate without mirroring the full backend ACL.
export const ROLE_REMOVE_EFFECTS_TH = {
  MANAGER: ["ไม่สามารถเปิดรายงานของสาขานี้", "ไม่สามารถจัดการพนักงานและตารางงาน"],
  DOCTOR: ["ไม่สามารถเปิดเคสและบันทึกผลตรวจในสาขานี้"],
  COUNTER: ["ไม่สามารถลงทะเบียนคนไข้และจัดการนัดหมายในสาขานี้"],
  ASSISTANT: ["ไม่สามารถเข้าถึงงานข้างเก้าอี้ในสาขานี้"],
  HEADCOUNTER: ["ไม่สามารถดูแลงานหน้าร้านของสาขานี้"],
  STOCK_MANAGER: ["ไม่สามารถจัดการคลังและอนุมัติการเบิกจ่ายของสาขานี้"],
  STOCK_STAFF: ["ไม่สามารถเบิก-จ่ายสต๊อกของสาขานี้"],
  RDM: ["ไม่สามารถดูแลสาขาในขอบเขตที่เคยกำหนด"],
  ACCOUNTANT: ["ไม่สามารถเข้าถึงข้อมูลการเงินระดับคลินิก"],
  PROCUREMENT: ["ไม่สามารถดูแลการจัดซื้อระดับคลินิก"],
  C_LEVEL: ["ไม่สามารถเข้าถึงสิทธิ์ผู้บริหารระดับคลินิก"],
};

export const PERMISSIONS = {
  BRANCH_ROLE_ALL: "branch.role_all",
  BRANCH_ROLE_ALL_WITH_STOCK: "branch.role_all.with_stock",
  BRANCH_ROLE_MANAGER: "branch.role_manager",
  BRANCH_ROLE_HEADCOUNTER: "branch.role_headcounter",
  BRANCH_ROLE_COUNTER: "branch.role_counter",
  BRANCH_ROLE_DOCTOR: "branch.role_doctor",
  STOCK_ROLE_ALL: "stock.role_all",
  STOCK_ROLE_MANAGER: "stock.role_manager",
  STOCK_HQ_APPROVE: "stock.hq_approve",
  STOCK_VIEW_DETAIL: "stock.view_detail",
  MIXED_HEADCOUNTER_AND_STOCK: "mixed.headcounter_and_stock",
  MIXED_HEADCOUNTER_AND_STOCK_MANAGER: "mixed.headcounter_and_stock_manager",
  USER_VIEW_BRANCH: "user.view.branch",
  USER_VIEW_CLINIC: "user.view.clinic",
  USER_REMOVE_BRANCH: "user.remove.branch",
  USER_REMOVE_CLINIC: "user.remove.clinic",
  DASHBOARD_CLINIC_VIEW: "dashboard.clinic.view",
  DASHBOARD_BRANCH_VIEW: "dashboard.branch.view",
};

export const PERMISSION_MAP = {
  // Phase 0 — branch-scoped, mirrored for cross-checks
  "branch.role_all": [1, 2, 3, 4, 5],
  "branch.role_all.with_stock": [1, 2, 3, 4, 5, 6, 7],
  "branch.role_manager": [1],
  "branch.role_headcounter": [1, 5],
  "branch.role_counter": [1, 3, 5],
  "branch.role_doctor": [2],
  "stock.role_all": [6, 7],
  "stock.role_manager": [6],
  "mixed.headcounter_and_stock": [1, 5, 6, 7],
  "mixed.headcounter_and_stock_manager": [1, 5, 6],

  // Clinic-scoped views / removals
  "user.view.branch": [1, 101, 102],
  "user.view.clinic": [101, 102],
  "user.remove.branch": [1, 101, 102],
  "user.remove.clinic": [102],

  // Executive dashboard
  "dashboard.clinic.view": [102],
  "dashboard.branch.view": [1, 101, 102],

  // Auto-generated user.assign.* — mirrors anti-escalation matrix
  "user.assign.MANAGER": [101, 102],
  "user.assign.DOCTOR": [1, 101, 102],
  "user.assign.COUNTER": [1, 101, 102],
  "user.assign.ASSISTANT": [1, 101, 102],
  "user.assign.HEADCOUNTER": [1, 101, 102],
  "user.assign.STOCK_MANAGER": [1, 101, 102],
  "user.assign.STOCK_STAFF": [1, 101, 102],
  "user.assign.RDM": [102],
  "user.assign.ACCOUNTANT": [102],
  "user.assign.PROCUREMENT": [102],
  // Server-locked. Bootstrap script seeds C_LEVEL; never assignable from UI.
  "user.assign.C_LEVEL": [],
};

// Anti-escalation matrix: which role keys can assign which targets.
export const ASSIGNABLE = {
  C_LEVEL: [
    "RDM",
    "ACCOUNTANT",
    "PROCUREMENT",
    "MANAGER",
    "DOCTOR",
    "COUNTER",
    "ASSISTANT",
    "HEADCOUNTER",
    "STOCK_MANAGER",
    "STOCK_STAFF",
  ],
  RDM: [
    "MANAGER",
    "DOCTOR",
    "COUNTER",
    "ASSISTANT",
    "HEADCOUNTER",
    "STOCK_MANAGER",
    "STOCK_STAFF",
  ],
  MANAGER: [
    "DOCTOR",
    "COUNTER",
    "ASSISTANT",
    "HEADCOUNTER",
    "STOCK_MANAGER",
    "STOCK_STAFF",
  ],
};
